import parse, { Element } from 'html-react-parser';
import { FC, ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDmmNavContext } from '@/contexts/dmmNav';
import {
  HEADER_ID,
  overwriteLinkRouting,
  overwriteLogoRouting,
  rewriteAccountsUrl,
} from '@/lib/dmm/nav';

import { AntiLayoutShift, Header, HeaderFallback } from './style';

interface DmmNavProps {
  isMobile: boolean;
  children: ReactNode;
}

const DmmNav: FC<DmmNavProps> = ({ children, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dmmNav = useDmmNavContext();

  const isRenderReady = dmmNav.isFetched && dmmNav.isLoadedCSS;

  useEffect(() => {
    if (!dmmNav.isFetched) {
      dmmNav.fetch(isMobile);
    }
  }, []);

  useEffect(() => {
    if (dmmNav.isFetched && !dmmNav.isLoadedCSS) {
      dmmNav.loadCSS();
    }
  }, [dmmNav.isFetched]);

  useEffect(() => {
    if (isRenderReady) {
      overwriteLogoRouting(navigate);
      overwriteLinkRouting(navigate);
    }
  }, [isRenderReady]);

  useEffect(() => {
    if (isRenderReady) {
      dmmNav.lazyLoadScripts();
    }
  }, [isRenderReady]);

  useEffect(() => {
    if (isRenderReady) {
      rewriteAccountsUrl();
    }
  }, [location.pathname, location.search, isRenderReady]);

  const wrapMainContents = useCallback(
    (_children: ReactElement) => {
      const wrapper = `${
        dmmNav.element?.contentStart || '<div>'
      }<div id="dmm-nav-main-content-dummy"></div>${
        dmmNav.element?.contentEnd || '</div>'
      }`;
      return parse(wrapper, {
        replace: domNode => {
          const elm = domNode as Element;
          if (elm.attribs) {
            if (
              elm.name === 'div' &&
              elm.attribs.id === 'dmm-nav-main-content-dummy'
            ) {
              return _children;
            }
          }
          return null;
        },
      });
    },
    [dmmNav.element?.contentStart, dmmNav.element?.contentEnd],
  );

  return (
    <>
      <div>
        {isRenderReady ? (
          <Header
            id={HEADER_ID}
            dangerouslySetInnerHTML={{
              __html: dmmNav.element!.header! || '',
            }}
            isMobile={isMobile}
          />
        ) : (
          <HeaderFallback isMobile={isMobile} />
        )}
      </div>
      {wrapMainContents(
        <AntiLayoutShift isMobile={isMobile}>{children}</AntiLayoutShift>,
      )}
    </>
  );
};

export default DmmNav;
